import React from "react";
import { summariseDayExtras } from "../../utils/salesReportTotals";

// The extra sections of the Daily Sales Report, shared by the thermal (80mm) and
// IMIN reports so both print exactly the same figures:
//   Sales summary | Returns & deleted | Collected vs credit | VAT by rate |
//   Category share | Top items
// Inline styles only (the report is also printed from an iframe that does not
// load the app's stylesheets). `fontSize` is the base size in px: ~12 for the
// 80mm preview, larger for the IMIN bitmap print.

const money = (symbol, value) => `${symbol}${Number(value || 0).toFixed(2)}`;
const qtyText = (value) => Number(Number(value || 0).toFixed(3)).toString();

export default function SalesReportSections({
  totals,
  dayExtras,
  showDayExtras = true,
  currencySymbol = "",
  taxLabel = "VAT",
  fontSize = 12,
}) {
  if (!totals || !totals.invoiceCount) return null;
  const extras = summariseDayExtras(dayExtras);
  const small = Math.max(10, Math.round(fontSize * 0.88));

  const divider = { borderTop: "1px dashed #333", margin: `${Math.round(fontSize * 0.5)}px 0` };
  const row = { display: "flex", justifyContent: "space-between", gap: 8, fontSize };
  const heading = { fontWeight: "bold", fontSize, margin: `${Math.round(fontSize * 0.2)}px 0` };
  const Row = ({ label, value, sub }) => (
    <div style={{ ...row, ...(sub ? { fontSize: small, paddingLeft: 10 } : {}) }}>
      <span>{label}</span>
      <span style={{ whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );

  const hasVat = (totals.vatByRate || []).some((r) => r.vat > 0.004);
  const categories = totals.categoryList || [];
  const categoryTotal = categories.reduce((sum, c) => sum + c.amount, 0) || 1;
  const showReturnsBlock = showDayExtras && extras.loaded;
  const showReceivedBlock = showDayExtras && extras.loaded && extras.receivedCount > 0;

  return (
    <div data-testid="sales-report-sections">
      <div style={divider} />
      <div style={heading}>Sales Summary</div>
      <Row
        label="Invoices"
        value={`${totals.invoiceCount}${totals.firstInvoice ? ` (${totals.firstInvoice} to ${totals.lastInvoice})` : ""}`}
      />
      <Row label="Items sold" value={qtyText(totals.itemsSold)} />
      <Row label="Average bill" value={money(currencySymbol, totals.averageBill)} />
      {totals.discountCount > 0 && <Row label="Discounted invoices" value={totals.discountCount} />}

      {showReturnsBlock && (extras.returnsCount > 0 || extras.deletedCount > 0) && (
        <>
          <div style={divider} />
          <div style={heading}>Returns &amp; Deleted</div>
          {extras.returnsCount > 0 && (
            <>
              <Row label={`Returns (${extras.returnsCount})`} value={`-${money(currencySymbol, extras.returnsTotal)}`} />
              <Row
                label="Net after returns"
                value={money(currencySymbol, totals.netSales - extras.returnsTotal)}
              />
            </>
          )}
          {extras.deletedCount > 0 && (
            <Row label={`Deleted invoices (${extras.deletedCount})`} value={money(currencySymbol, extras.deletedTotal)} />
          )}
        </>
      )}

      <div style={divider} />
      <div style={heading}>Collected vs Credit</div>
      <Row label="Paid at sale" value={money(currencySymbol, totals.collectedAtSale)} />
      <Row
        label={`Credit given, unpaid${totals.creditCount ? ` (${totals.creditCount})` : ""}`}
        value={money(currencySymbol, totals.creditDue)}
      />
      {showReceivedBlock && (
        <>
          <Row
            label={`Received on older invoices (${extras.receivedCount})`}
            value={money(currencySymbol, extras.receivedTotal)}
          />
          {Object.entries(extras.receivedByMode).map(([mode, amount]) => (
            <Row key={mode} sub label={mode} value={money(currencySymbol, amount)} />
          ))}
        </>
      )}

      {hasVat && (
        <>
          <div style={divider} />
          <div style={heading}>{taxLabel} by Rate</div>
          {totals.vatByRate.map((r) => (
            <div key={String(r.rate)} style={{ ...row, fontSize: small }}>
              <span>{r.rate === null ? "Rate not recorded" : `${Number(r.rate)}%`}</span>
              <span style={{ whiteSpace: "nowrap" }}>
                {money(currencySymbol, r.taxable)} + {money(currencySymbol, r.vat)}
              </span>
            </div>
          ))}
          <div style={{ fontSize: small, color: "#555" }}>Taxable amount + {taxLabel}</div>
        </>
      )}

      {categories.length > 0 && (
        <>
          <div style={divider} />
          <div style={heading}>Category Share</div>
          {categories.map((c) => (
            <div key={c.name} style={{ ...row, fontSize: small }}>
              <span style={{ overflowWrap: "anywhere" }}>{c.name}</span>
              <span style={{ whiteSpace: "nowrap" }}>
                {qtyText(c.qty)} · {((c.amount / categoryTotal) * 100).toFixed(1)}%
              </span>
            </div>
          ))}
        </>
      )}

      {(totals.topItems || []).length > 0 && (
        <>
          <div style={divider} />
          <div style={heading}>Top Items</div>
          {totals.topItems.map((item, index) => (
            <div key={`${item.name}-${index}`} style={{ ...row, fontSize: small }}>
              <span style={{ overflowWrap: "anywhere" }}>
                {index + 1}. {item.name}
              </span>
              <span style={{ whiteSpace: "nowrap" }}>
                {qtyText(item.qty)} · {money(currencySymbol, item.amount)}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
